/**
 * Строка смешанного списка Диска (файл или папка) и связанные хелперы —
 * общие для таблицы (DrivePage) и вида «Плитки» (DriveTilesView).
 */
import type { UploadedFile } from '../../../schemas/uploaded_file.schema';
import { isImageMime } from './driveFiles';

/** Автор файла — безопасное подмножество identity_users (withCreatedBy). */
export interface DriveFileAuthor {
  id: number;
  name: string | null;
  email: string | null;
  username: string | null;
}

/**
 * Строка среза DriveEntryRepository: файл, папка или ярлык. Служебные поля —
 * виртуальные колонки среза (withColumn), в тип файловой схемы не входят,
 * поэтому опциональны.
 */
export type DriveRow = UploadedFile & {
  kind?: 'folder' | 'file' | 'shortcut';
  entry_parent_id?: number | null;
  entry_access?: string | null;
  entry_is_system?: boolean | null;
  /** id реального файла строки: файл → сам, ярлык → оригинал, папка → NULL. */
  entry_file_id?: number | null;
  created_by?: DriveFileAuthor | null;
};

/** Достаёт виртуальную колонку created_by из строки (withColumn не входит в тип схемы). */
export function pickAuthor(row: DriveRow): DriveFileAuthor | null {
  return row.created_by ?? null;
}

export function isFolderRow(row: DriveRow): boolean {
  return row.kind === 'folder';
}

/** Ярлык — read-only указатель на файл в дополнительной папке (не выделяется, открывает оригинал). */
export function isShortcutRow(row: DriveRow): boolean {
  return row.kind === 'shortcut';
}

/**
 * Стабильный row-id строки Диска (`folder-42` / `file-42`). Единая формула для
 * RepositoryTable (getRowId) и вида «Плитки» — благодаря ей выделение в
 * контроллере совпадает между видами и переживает переключение.
 */
export function driveRowId(row: DriveRow): string {
  return `${row.kind ?? 'file'}-${row.id}`;
}

/**
 * id реального файла строки Диска для выбора в пикере: файл → его id,
 * ярлык → id файла-ОРИГИНАЛА (entry_file_id, а не id строки-ярлыка), папка → null.
 * Один файл, выбранный и как оригинал, и через ярлык, даёт один и тот же id.
 */
export function resolveFileId(row: DriveRow): number | null {
  if (isFolderRow(row)) return null;
  if (isShortcutRow(row)) return row.entry_file_id ?? null;
  return row.entry_file_id ?? row.id;
}

/** Кадр ленты просмотрщика изображений, собранный из строк Диска. */
export interface DriveImageStripItem {
  /** id файла-оригинала (ярлык схлопывается в оригинал). */
  fileId: number;
  publicUrl: string;
  name: string;
}

/**
 * Лента изображений для просмотрщика: картинки из строк текущей страницы +
 * индекс кликнутой. Ярлыки включаются (несут public_url оригинала), но файл,
 * видимый и сам, и через ярлык, попадает в ленту ОДИН раз (дедуп по
 * файлу-оригиналу — иначе «далее» показывало бы тот же кадр повторно).
 *
 * null — кликнутая строка не изображение или без ссылки: вызывающий открывает
 * файл обычным путём (новая вкладка).
 */
export function buildImageStrip(
  rows: DriveRow[],
  clicked: DriveRow
): { images: DriveImageStripItem[]; index: number } | null {
  const clickedFileId = resolveFileId(clicked);
  if (clickedFileId == null || !isImageMime(clicked.mime_type) || !clicked.public_url) {
    return null;
  }

  const images: DriveImageStripItem[] = [];
  const seen = new Set<number>();
  for (const row of rows) {
    if (isFolderRow(row) || !isImageMime(row.mime_type) || !row.public_url) continue;
    const fileId = resolveFileId(row);
    if (fileId == null || seen.has(fileId)) continue;
    seen.add(fileId);
    images.push({
      fileId,
      publicUrl: row.public_url,
      name: row.original_name ?? row.filename,
    });
  }

  const index = images.findIndex(image => image.fileId === clickedFileId);
  if (index === -1) return null; // кликнутой нет в наборе (рассинхрон) — не гадаем
  return { images, index };
}

/** id файлов из набора row-id выделения (папки — с префиксом folder- — отсекаются). */
export function fileIdsFromRowIds(rowIds: string[]): number[] {
  const ids: number[] = [];
  for (const rowId of rowIds) {
    if (!rowId.startsWith('file-')) continue;
    const id = Number(rowId.slice('file-'.length));
    if (!Number.isNaN(id)) ids.push(id);
  }
  return ids;
}
